import "./tailwind.css";
import React from "react";
import { createRoot } from "react-dom/client";
import "semantic-ui-css/semantic.min.css";
import "font-awesome/css/font-awesome.min.css";
import { ConfigProvider } from "antd";
import esES from "antd/locale/es_ES";
import "moment/locale/es";
import mapboxgl from 'mapbox-gl';
import ReactDOM from "react-dom";
import App from "./App";
import { ApolloProvider, HttpLink, InMemoryCache, ApolloClient } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { createGlobalStyle } from "styled-components";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// React 19 legacy compatibility is handled via vite.config.ts alias to src/react-dom-proxy.js

const GlobalStyle = createGlobalStyle`
    html,body{ height:100%; margin: 0; padding: 0; }
`;

import axios from 'axios';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

const cache = new InMemoryCache();
const apiUrl = import.meta.env.PROD ? window.location.origin : (import.meta.env.VITE_API_URL || 'http://localhost:8080');
if (apiUrl) {
  axios.defaults.baseURL = apiUrl;
}
axios.defaults.headers.common['ngrok-skip-browser-warning'] = 'true';

/**
 * La sesión viaja en cada petición, tanto por REST como por GraphQL.
 *
 * Antes no viajaba en ninguna de las dos: el backend dejaba pasar sin token y
 * nadie lo notó. En cuanto los resolvers empezaron a exigir sesión, la pantalla
 * de usuarios dejó de funcionar, porque el token estaba guardado pero nunca se
 * enviaba.
 *
 * Se lee en cada petición y no una sola vez al arrancar: quien inicia sesión
 * después de cargada la página tiene un token nuevo, y fijarlo al arranque
 * dejaría al usuario mandando para siempre el de antes, o ninguno.
 */
const tokenDeSesion = () => {
  try {
    const t = localStorage.getItem('token');
    return t ? `Bearer ${t}` : null;
  } catch {
    return null;
  }
};

axios.interceptors.request.use((config) => {
  const t = tokenDeSesion();
  if (t) config.headers.Authorization = t;
  return config;
});

const conSesion = setContext((_, { headers }) => {
  const t = tokenDeSesion();
  return {
    headers: {
      ...headers,
      'ngrok-skip-browser-warning': 'true',
      ...(t ? { Authorization: t } : {})
    }
  };
});

export const client = new ApolloClient({
  cache,
  link: conSesion.concat(new HttpLink({ uri: `${apiUrl}/graphql` })),
});

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

const rootElement = document.getElementById("root");

if (rootElement) {
  const root = createRoot(rootElement);
  root.render(
    <ApolloProvider client={client}>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider locale={esES}>
          <React.Fragment>
            <GlobalStyle />
            <App />
          </React.Fragment>
        </ConfigProvider>
      </QueryClientProvider>
    </ApolloProvider>
  );
} else {
  console.error("SALE_SPHERE_V3005_PROVIDERS: No se encontró #root");
}

